import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useParams } from 'umi';
import Permission from '@/pages/dashboard/components/NoPermission';
import PermissionNew from '@/pages/dashboard/components/NoPermissionNew';
import Loading from '@/pages/dashboard/components/Loadings';
import { IframeUrl } from '@/pages/home/components/IframeUrl';
import { getUtilAuthToken } from '@/pages/home/services/authPath';
import { PAGE_TYPE_OUT_PATH } from '@/pages/dashboard/_config/constant';
import {
  analysisUrlInfo,
  Permission as GamePermission,
} from '@/pages/dashboard/utils/urlCollection';
import { IS_GLOBAL } from '@/pages/home/_config/config';

interface IReportInfo {
  loading: boolean;
  error: string;
  authUrl: string;
  permissionDenied: boolean;
}

const INIT_REPORT_INFO: IReportInfo = {
  loading: false,
  error: '',
  authUrl: '',
  permissionDenied: false,
};

/**
 * 通用鉴权 iframe 全屏页：校验 gameCode 权限后，通过 gettoken 获取最新 token 并嵌入目标链接。
 * 路由：/dashboard/game/:gamecode/authEmbed?target=<encodedUrl>
 */
const AuthEmbed = () => {
  const [reportInfo, setReportInfo] = useState<IReportInfo>(INIT_REPORT_INFO);
  const location = useLocation();
  const { gamecode } = useParams() as any;
  const mountRef = useRef<boolean>(true);

  const setState = useCallback((params: Partial<IReportInfo> = {}) => {
    setReportInfo({
      ...INIT_REPORT_INFO,
      ...params,
    });
  }, []);

  const targetUrl = React.useMemo(() => {
    const search = new URLSearchParams(location.search);
    return decodeURIComponent(search.get('target') || '');
  }, [location.search]);

  const fetchTokenAndBuildUrl = useCallback(async () => {
    if (!gamecode) {
      setState({ error: '未获取到游戏信息' });
      return;
    }

    if (!targetUrl) {
      setState({ error: '未获取到目标链接' });
      return;
    }

    setState({ loading: true });

    try {
      const permissionResult = await analysisUrlInfo({
        url: `/dashboard/game/${gamecode}/authEmbed`,
      });

      if (!mountRef.current) return;

      if (permissionResult.permission !== GamePermission.HasPermission) {
        setState({ permissionDenied: true });
        return;
      }

      const res = await getUtilAuthToken({
        res_id: 'dashboard_pc',
        game_code: gamecode,
        menu_id: '',
        page_type: PAGE_TYPE_OUT_PATH,
        path: targetUrl,
      });

      if (!mountRef.current) return;

      const token = res?.data?.token;
      if (!token) {
        setState({ error: '获取鉴权token失败' });
        return;
      }

      const separator = targetUrl.includes('?') ? '&' : '?';
      const authUrl = `${targetUrl}${separator}token=${encodeURIComponent(token)}`;
      setState({ authUrl });
    } catch (err) {
      if (!mountRef.current) return;
      setState({ error: '获取鉴权信息失败' });
    }
  }, [targetUrl, gamecode, setState]);

  useEffect(() => {
    mountRef.current = true;
    fetchTokenAndBuildUrl();
    return () => {
      mountRef.current = false;
    };
  }, [fetchTokenAndBuildUrl]);

  if (reportInfo.loading) {
    return <Loading />;
  }

  if (reportInfo.permissionDenied) {
    return <PermissionNew gameCode={gamecode} showButton={!IS_GLOBAL} />;
  }

  if (reportInfo.error) {
    return <Permission type="error" textCN={reportInfo.error} />;
  }

  return <IframeUrl url={reportInfo.authUrl} />;
};

export default AuthEmbed;
